import { useMemo, useState } from 'react'
import { Car, ChevronDown, ChevronRight, Layers } from 'lucide-react'
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import { EditOwnerDialog } from './EditOwnerDialog'
import { OwnerDocumentsDialog } from './OwnerDocumentsDialog'
import { OwnerPipelineDialog } from './OwnerPipelineDialog'
import { type OwnerRow, formatDate, initials } from '../../utils/owners'
import {
  PHASES,
  daysLabel,
  groupByStage,
  isStale,
  stageLabel,
  stageTone,
  trackLabel,
} from '../../utils/ownerBoard'
import { OwnerFinesDialog } from '../fines/PersonFines'

// Owners grouped by stage, stage groups under their phase, every group
// closeable. Parked and closed groups start closed: they are reference, not
// work. A stage the operator has filtered to is always open.
export function OwnerBoardGroups({
  rows,
  showTrack,
  forceOpen,
  canEditOwners,
  onReload,
}: {
  rows: OwnerRow[]
  showTrack: boolean
  forceOpen: string[]
  canEditOwners: boolean
  onReload: () => void
}) {
  const groups = useMemo(
    () =>
      groupByStage(
        rows.map((r) => ({ ...r, days_in_stage: r.days_in_stage ?? null })),
      ),
    [rows],
  )
  const [toggled, setToggled] = useState<Record<string, boolean>>({})

  const isOpen = (stage: string, phase: string) => {
    if (forceOpen.includes(stage)) return true
    if (stage in toggled) return toggled[stage]!
    return PHASES.find((p) => p.code === phase)?.openByDefault ?? true
  }

  let lastPhase = ''
  return (
    <div className="pb-6">
      {groups.map((g) => {
        const open = isOpen(g.stage, g.phase)
        const phase = PHASES.find((p) => p.code === g.phase)
        const phaseHeading = g.phase !== lastPhase ? phase?.label : null
        lastPhase = g.phase
        const stale = g.rows.filter((r) => isStale(r)).length
        const oldest = g.rows[0]?.days_in_stage ?? null
        return (
          <section key={g.stage}>
            {phaseHeading && (
              <div className="bg-muted/40 px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {phaseHeading}
              </div>
            )}
            <button
              type="button"
              onClick={() => setToggled((t) => ({ ...t, [g.stage]: !open }))}
              className="flex w-full items-center gap-2 border-b border-border px-4 py-2 text-left hover:bg-muted/40"
            >
              {open ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
              <Chip tone={stageTone(g.stage)}>{stageLabel(g.stage)}</Chip>
              <span className="text-sm font-semibold tabular-nums text-foreground">{g.rows.length}</span>
              {stale > 0 && (
                <span className="text-[11px] text-amber-700 dark:text-amber-300 tabular-nums">{stale} stuck</span>
              )}
              <span className="ml-auto text-[11px] text-muted-foreground">
                longest {daysLabel(oldest)}
              </span>
            </button>
            {open && (
              <ul>
                {g.rows.map((owner) => (
                  <OwnerBoardRow
                    key={owner.application_id}
                    owner={owner as OwnerRow}
                    showTrack={showTrack}
                    canEditOwners={canEditOwners}
                    onReload={onReload}
                  />
                ))}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}

function vehicleDetail(owner: OwnerRow): string | null {
  if (owner.live_plates) return owner.live_plates
  if (owner.owner_model === 'asset_finance') {
    return owner.cars_requested ? `${owner.cars_requested} car(s) wanted` : null
  }
  const car = [owner.vehicle_year, owner.vehicle_make !== 'Other' ? owner.vehicle_make : null, owner.vehicle_model]
    .filter(Boolean)
    .join(' ')
  return car || null
}

function OwnerBoardRow({
  owner,
  showTrack,
  canEditOwners,
  onReload,
}: {
  owner: OwnerRow
  showTrack: boolean
  canEditOwners: boolean
  onReload: () => void
}) {
  const detail = vehicleDetail(owner)
  const stale = isStale({
    owner_model: owner.owner_model,
    current_status: owner.current_status,
    days_in_stage: owner.days_in_stage ?? null,
  })
  // A live car under a lower track than the one this person is shown in.
  // live_plates already carries this track's live and offboarding cars, so
  // anything in all_live_plates beyond those is a car under another track.
  const here = new Set((owner.live_plates ?? '').split(',').map((p) => p.trim()).filter(Boolean))
  const elsewhere = (owner.all_live_plates ?? '')
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p && !here.has(p.replace(/\s*\(.*\)$/, '')))
  const liveElsewhere = elsewhere.length ? elsewhere.join(', ') : null
  const others = owner.other_applications ?? []
  const othersTitle = others
    .map((o) => `${trackLabel(o.model)}: ${stageLabel(o.status)}${o.reason ? ` (${o.reason})` : ''}`)
    .join('\n')

  return (
    <li className="flex items-start gap-3 border-b border-border px-4 py-2.5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[11px] font-semibold text-[hsl(var(--brand-soft-foreground))]">
        {initials(owner.full_name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm font-medium text-foreground">{owner.full_name || 'Unnamed owner'}</p>
          <span
            className={cn(
              'shrink-0 text-xs tabular-nums',
              stale ? 'font-medium text-amber-700 dark:text-amber-300' : 'text-muted-foreground',
            )}
            title={owner.in_stage_since ? `In this stage since ${formatDate(owner.in_stage_since)}` : undefined}
          >
            {daysLabel(owner.days_in_stage)} in stage
          </span>
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {[owner.primary_phone, owner.primary_email].filter(Boolean).join(' · ') || '—'}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {showTrack && <Chip tone="neutral">{trackLabel(owner.owner_model)}</Chip>}
          {showTrack && <Chip tone={stageTone(owner.current_status)}>{stageLabel(owner.current_status)}</Chip>}
          {detail && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
              <Car className="h-3 w-3" />
              {detail}
            </span>
          )}
          {liveElsewhere && (
            <Chip tone="green" className="text-[10px]">
              Live car: {liveElsewhere}
            </Chip>
          )}
          {others.length > 0 && (
            <span
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"
              title={othersTitle}
            >
              <Layers className="h-3 w-3" />
              {others.length} other application{others.length === 1 ? '' : 's'}
            </span>
          )}
          {owner.meeting_at && owner.current_status === 'meeting_booked' && (
            <span className="text-[11px] text-muted-foreground">Meeting {formatDate(owner.meeting_at)}</span>
          )}
          <span className="ml-auto flex items-center gap-2">
            {/* Manage My Car runs its whole journey from this panel. The other
                tracks are still driven elsewhere, so they do not get it yet. */}
            {owner.owner_model === 'brought_in' && owner.application_id && (
              <OwnerPipelineDialog owner={owner} onChanged={onReload} />
            )}
            <OwnerDocumentsDialog owner={owner} />
            <OwnerFinesDialog personId={owner.person_id ?? null} name={owner.full_name ?? ''} />
            {canEditOwners && <EditOwnerDialog owner={owner} onSaved={onReload} />}
          </span>
        </div>
      </div>
    </li>
  )
}
